import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowDown } from "lucide-react";
import { useLanguage, fmtEUR } from "@/i18n";
import { api } from "@/api";
import { Reveal, MaskedLines, Overline } from "@/components/motion";
import MarqueeRibbon from "@/components/MarqueeRibbon";

const HERO_IMG = "https://images.pexels.com/photos/8499547/pexels-photo-8499547.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const Home = () => {
  const { lang, clang, cf, t } = useLanguage();
  const [stats, setStats] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const { scrollY } = useScroll();
  const yImg = useTransform(scrollY, [0, 700], [0, 110]);

  useEffect(() => {
    api.get("/stats/public").then((r) => setStats(r.data)).catch(() => {});
    api.get("/campaigns").then((r) => setCampaigns(r.data.filter((c) => c.status === "active").slice(0, 3))).catch(() => {});
    api.post("/track/visit").catch(() => {});
  }, []);

  const pillars = t("pillars.items");

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-[100svh] flex items-center overflow-hidden pt-24">
        <motion.div style={{ y: yImg }} className="absolute right-[-4%] top-[10%] w-[40vw] max-w-[540px] hidden lg:block z-0">
          <img src={HERO_IMG} alt="Young student" data-testid="hero-portrait" className="w-full aspect-[3/4] object-cover hero-clip" />
        </motion.div>
        <div className="relative z-10 max-w-[1500px] mx-auto px-6 md:px-12 w-full pb-24">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1, duration: 0.8 }}
            className="text-xs uppercase tracking-[0.25em] font-semibold text-clay mb-8"
            data-testid="hero-overline"
          >
            {t("hero.overline")}
          </motion.p>
          <MaskedLines
            lines={t("hero.lines")}
            className="font-serif font-semibold text-forest text-[13vw] lg:text-[9.5vw] leading-[0.9] tracking-tighter max-w-[80vw]"
          />
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.8 }}
            className="mt-10 max-w-xl text-base lg:text-lg leading-relaxed text-sage"
            data-testid="hero-sub"
          >
            {t("hero.sub")}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.8 }}
            className="mt-10 flex flex-wrap gap-4"
          >
            <Link to="/campaigns" data-testid="hero-cta-primary" className="inline-flex items-center gap-2 bg-forest text-cream px-8 py-4 rounded-full font-medium hover:bg-clay hover:-translate-y-1 transition-transform duration-300">
              {t("hero.ctaPrimary")} <ArrowUpRight size={18} />
            </Link>
            <Link to="/about" data-testid="hero-cta-secondary" className="inline-flex items-center gap-2 border border-forest/30 text-forest px-8 py-4 rounded-full font-medium hover:border-clay hover:text-clay hover:-translate-y-1 transition-all duration-300">
              {t("hero.ctaSecondary")}
            </Link>
          </motion.div>
        </div>
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2.4 }} className="absolute bottom-8 left-1/2 -translate-x-1/2 text-forest/40">
          <ArrowDown size={20} />
        </motion.div>
      </section>

      <MarqueeRibbon />

      {/* STATS */}
      <section className="border-b border-forest/10" data-testid="stats-strip">
        <div className="max-w-[1500px] mx-auto px-6 md:px-12 py-20 grid grid-cols-2 lg:grid-cols-4 gap-10">
          {[
            { value: stats ? fmtEUR(stats.total_raised, lang) : "—", label: t("stats.raised"), id: "stat-raised" },
            { value: stats ? String(stats.campaigns) : "—", label: t("stats.campaigns"), id: "stat-campaigns" },
            { value: stats ? String(stats.members) : "—", label: t("stats.members"), id: "stat-members" },
            { value: stats ? String(stats.donations) : "—", label: t("stats.donations"), id: "stat-donations" },
          ].map((s, i) => (
            <Reveal key={s.id} delay={i * 0.1}>
              <p data-testid={s.id} className="font-serif text-4xl md:text-6xl font-semibold text-forest">{s.value}</p>
              <p className="mt-2 text-sm text-sage">{s.label}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* PILLARS */}
      <section className="max-w-[1500px] mx-auto px-6 md:px-12 py-28" data-testid="pillars-section">
        <Reveal>
          <Overline>{t("pillars.overline")}</Overline>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight text-forest max-w-3xl">{t("pillars.title")}</h2>
        </Reveal>
        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6">
          {pillars.map((p, i) => (
            <Reveal key={p.num} delay={i * 0.12}>
              <div data-testid={`pillar-${p.num}`} className="group relative border border-forest/15 p-10 h-full hover:-translate-y-2 hover:border-clay/50 transition-transform duration-500 bg-cream">
                <span className="font-serif text-7xl font-semibold text-forest/10 group-hover:text-clay/25 transition-colors duration-500">{p.num}</span>
                <h3 className="mt-6 font-serif text-2xl md:text-3xl text-forest">{p.title}</h3>
                <p className="mt-4 text-sm md:text-base leading-relaxed text-sage">{p.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* FEATURED CAMPAIGNS */}
      <section className="bg-sand/60 border-y border-forest/10" data-testid="featured-campaigns">
        <div className="max-w-[1500px] mx-auto px-6 md:px-12 py-28">
          <Reveal className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <Overline>{t("featured.overline")}</Overline>
              <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight text-forest">{t("featured.title")}</h2>
            </div>
            <Link to="/campaigns" data-testid="featured-view-all" className="inline-flex items-center gap-2 text-forest font-medium border-b-2 border-clay pb-1 hover:text-clay transition-colors duration-300">
              {t("featured.viewAll")} <ArrowUpRight size={18} />
            </Link>
          </Reveal>
          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6">
            {campaigns.map((c, i) => {
              const pct = c.goal_amount > 0 ? Math.min(100, Math.round((c.raised_amount / c.goal_amount) * 100)) : 0;
              return (
                <Reveal key={c.id} delay={i * 0.12} className={i === 0 ? "md:col-span-2 md:row-span-1" : ""}>
                  <Link to={`/campaigns/${c.slug || c.id}`} data-testid={`featured-campaign-${c.id}`} className="group block h-full border border-forest/15 bg-cream overflow-hidden hover:-translate-y-2 transition-transform duration-500">
                    <div className={`overflow-hidden ${i === 0 ? "aspect-[21/9]" : "aspect-[4/3]"}`}>
                      <img src={c.image_url} alt={cf(c, "title")} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    </div>
                    <div className="p-8">
                      <span className="text-[10px] uppercase tracking-[0.2em] font-semibold text-clay">{c.category}</span>
                      <h3 className="mt-2 font-serif text-2xl md:text-3xl text-forest group-hover:text-clay transition-colors duration-300">{cf(c, "title")}</h3>
                      <p className="mt-3 text-sm text-sage leading-relaxed line-clamp-2">{cf(c, "summary")}</p>
                      <div className="mt-6">
                        <div className="h-1 bg-forest/10 rounded-full overflow-hidden">
                          <div className="h-full bg-clay rounded-full" style={{ width: `${pct}%` }} />
                        </div>
                        <p className="mt-3 text-sm text-forest font-medium">
                          {fmtEUR(c.raised_amount, lang)} {t("featured.raised")} {t("featured.of")} {fmtEUR(c.goal_amount, lang)} — {pct}%
                        </p>
                      </div>
                    </div>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* MANIFESTO BAND */}
      <section className="bg-forest text-cream" data-testid="manifesto-band">
        <div className="max-w-[1500px] mx-auto px-6 md:px-12 py-32 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <Reveal className="lg:col-span-7">
            <Overline light>{t("manifesto.overline")}</Overline>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-7xl leading-[1.02] tracking-tight">{t("manifesto.title")}</h2>
          </Reveal>
          <Reveal delay={0.2} className="lg:col-span-5 flex flex-col justify-end">
            <p className="text-base lg:text-lg leading-relaxed text-cream/70">{t("manifesto.text")}</p>
            <Link to="/about" data-testid="manifesto-cta" className="mt-8 inline-flex w-fit items-center gap-2 bg-clay text-cream px-8 py-4 rounded-full font-medium hover:bg-clay-hover hover:-translate-y-1 transition-transform duration-300">
              {t("manifesto.cta")} <ArrowUpRight size={18} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-[1500px] mx-auto px-6 md:px-12 py-32 text-center" data-testid="home-cta">
        <Reveal>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight text-forest max-w-4xl mx-auto">{t("ctaBand.title")}</h2>
          <p className="mt-6 text-base lg:text-lg text-sage max-w-xl mx-auto">{t("ctaBand.text")}</p>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <Link to="/membership" data-testid="cta-member-btn" className="inline-flex items-center gap-2 bg-forest text-cream px-8 py-4 rounded-full font-medium hover:bg-clay hover:-translate-y-1 transition-transform duration-300">
              {t("ctaBand.member")} <ArrowUpRight size={18} />
            </Link>
            <Link to="/campaigns" data-testid="cta-donate-btn" className="inline-flex items-center gap-2 border border-forest/30 text-forest px-8 py-4 rounded-full font-medium hover:border-clay hover:text-clay hover:-translate-y-1 transition-all duration-300">
              {t("ctaBand.donate")}
            </Link>
          </div>
        </Reveal>
      </section>
    </div>
  );
};

export default Home;
